import {
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
  RestartAlt,
} from '@mui/icons-material';
import {
  Box,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
  lighten,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';

import GroupsMap, {
  GROUPS_RESET_BOUNDS_EVENT,
} from '../../../map/components/GroupsMap';
import GroupsLocationListWidget from './GroupsLocationListWidget';

interface GroupsMapFullscreenModalProps {
  open: boolean;
  onClose: () => void;
}

const GroupsMapFullscreenModal = ({
  open,
  onClose,
}: GroupsMapFullscreenModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [locationListHeight, setLocationListHeight] = useState<
    'collapsed' | 'expanded'
  >('expanded');

  const handleResetBounds = () => {
    window.dispatchEvent(new CustomEvent(GROUPS_RESET_BOUNDS_EVENT));
  };

  const toggleLocationList = () => {
    if (!isMobile) return;
    setLocationListHeight((prev) =>
      prev === 'collapsed' ? 'expanded' : 'collapsed'
    );
  };

  const handleLocationNavigate = () => {
    if (isMobile && locationListHeight === 'expanded') {
      setLocationListHeight('collapsed');
    }
  };

  const getLocationListHeight = () => {
    if (!isMobile) return 'auto';
    return locationListHeight === 'collapsed' ? '48px' : '70vh';
  };

  const getLocationListWidth = () => {
    if (isMobile) return '100%';
    return '400px';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: theme.tokens.colors.background,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, md: 3 },
            py: { xs: 1.5, md: 2 },
            borderBottom: `1px solid ${theme.tokens.colors.border}`,
            background: lighten(theme.palette.primary.main, 0.95),
          }}
        >
          <Typography
            variant='h6'
            sx={{
              fontWeight: theme.tokens.typography.fontWeight.semibold,
              color: theme.palette.primary.main,
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
            Groups Map
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title='Reset map view'>
              <IconButton onClick={handleResetBounds} size='small'>
                <RestartAlt />
              </IconButton>
            </Tooltip>
            <Tooltip title='Close fullscreen'>
              <IconButton onClick={onClose} size='small'>
                <Close />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flex: 1,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              overflow: 'hidden',
              height: {
                xs:
                  locationListHeight === 'collapsed'
                    ? 'calc(100% - 48px)'
                    : 'calc(100% - 70vh)',
                md: '100%',
              },
              zIndex: 0,
            }}
          >
            <GroupsMap />
          </Box>

          <Box
            sx={{
              width: getLocationListWidth(),
              height: getLocationListHeight(),
              borderTop: {
                xs: `1px solid ${theme.tokens.colors.border}`,
                md: 'none',
              },
              borderLeft: {
                xs: 'none',
                md: `1px solid ${theme.tokens.colors.border}`,
              },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              backgroundColor: theme.tokens.colors.card,
              position: { xs: 'absolute', md: 'relative' },
              bottom: { xs: 0, md: 'auto' },
              left: { xs: 0, md: 'auto' },
              right: { xs: 0, md: 'auto' },
              transition: 'height 0.3s ease-in-out',
              zIndex: 1000,
              boxShadow: {
                xs: theme.tokens.shadows.lg,
                md: 'none',
              },
            }}
          >
            {isMobile && (
              <Box
                onClick={toggleLocationList}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 1,
                  borderBottom:
                    locationListHeight !== 'collapsed'
                      ? `1px solid ${theme.tokens.colors.border}`
                      : 'none',
                  cursor: 'pointer',
                  backgroundColor: theme.tokens.alpha.black[10],
                  '&:active': {
                    backgroundColor: theme.tokens.alpha.black[30],
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: '40px',
                      height: '4px',
                      borderRadius: '2px',
                      backgroundColor: theme.tokens.colors.mutedForeground,
                      opacity: 0.5,
                    }}
                  />
                  {locationListHeight === 'collapsed' ? (
                    <KeyboardArrowUp fontSize='small' />
                  ) : (
                    <KeyboardArrowDown fontSize='small' />
                  )}
                </Box>
              </Box>
            )}

            {(!isMobile || locationListHeight !== 'collapsed') && (
              <Box
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <GroupsLocationListWidget
                  onItemClick={handleLocationNavigate}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default GroupsMapFullscreenModal;
